import React, { ReactElement } from 'react';
import {
  Card,
  Tab,
  Tabs,
  AppBar,
  CircularProgress,
  Box,
  Grid
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import Info from './CustomerTabs/Info/Info';
import AccountTabs from './CustomerTabs/Account';
import MembershipTab from './CustomerTabs/Membership';
import Waivers from './CustomerTabs/Waivers/Waivers';
// import CustomerMerge from './CustomerTabs/Merge/CustomerMerge';
import Appointments from './CustomerTabs/Appointments/Appointments';
import { TabPanel, a11yProps } from '../../../../common/utils/TabPanel';
import DocumentsTabs from './CustomerTabs/Documents';
import AdvancedMDTabs from './CustomerTabs/AdvancedMD';
// import { isManager } from '@/common/utils/utils';
import WellnessPlan from './CustomerTabs/WellnessPlan/WellnessPlan';
import { checkFeatureFlag } from '@/common/utils/featureFlags';
import NewAlertsNotes from './CustomerTabs/NewAlertsNotes';
import {
  useCustomerProfileQueryParams,
  TabKey
} from './useCustomerProfileQueryParams';

export type TabInfo = {
  access: boolean;
  displayName: string;
  urlPart: TabKey;
  content: ReactElement;
};

const useStyles: any = makeStyles(() => ({
  root: {
    height: 'calc(100vh - 120px)',
    paddingBottom: 16,
    paddingRight: 8,
    overflowY: 'auto'
  }
}));

const CustomerProfile = (props: any) => {
  const { refetchCustomer, customerAllData, drawerMode = false } = props;
  const [{ tab }, setQuery] = useCustomerProfileQueryParams();
  const classes = useStyles();

  const changeTab = (newTab: TabKey) => {
    if (newTab === tab) {
      return;
    }

    if (newTab === 'info') {
      refetchCustomer();
    }

    if (newTab) {
      setQuery({ tab: newTab }, 'replaceIn');
    }
  };

  let TabContents: TabInfo[] = [
    {
      access: true,
      displayName: 'Info',
      urlPart: 'info',
      content: (
        <Info
          customerAllData={customerAllData}
          changeTab={changeTab}
          drawerMode={drawerMode}
        />
      )
    },
    {
      access: true,
      displayName: 'Appointments',
      urlPart: 'appointments',
      content: <Appointments customerAllData={customerAllData?.customer} />
    },
    {
      access: true,
      displayName: 'Account',
      urlPart: 'account',
      content: <AccountTabs customerAllData={customerAllData?.customer} />
    },
    {
      access: true,
      displayName: 'Membership',
      urlPart: 'membership',
      content: <MembershipTab customerAllData={customerAllData?.customer} />
    },
    {
      access: true,
      displayName: 'Alerts & Notes',
      urlPart: 'alerts_and_notes',
      content: <NewAlertsNotes customer={customerAllData?.customer} />
    }
  ];
  if (checkFeatureFlag('WELLNESS_PLAN')) {
    TabContents.push({
      access: true,
      displayName: 'Wellness Plan',
      urlPart: 'wellness_plan',
      content: (
        <WellnessPlan
          customerAllData={customerAllData?.customer}
          refetchCustomer={refetchCustomer}
        />
      )
    });
  }
  TabContents = [
    ...TabContents,
    {
      access: true,
      displayName: 'Waivers',
      urlPart: 'waivers',
      content: <Waivers customerAllData={customerAllData?.customer} />
    },
    {
      access: true,
      displayName: 'Documents',
      urlPart: 'documents',
      content: <DocumentsTabs customerAllData={customerAllData?.customer} />
    } /* ,
    {
      access: isManager(),
      displayName: 'Merge',
      urlPart: 'merge',
      content: <CustomerMerge customerAllData={customerAllData} />
    }*/
  ];
  if (checkFeatureFlag('PATIENT_LINKING')) {
    TabContents.push({
      access: true,
      displayName: 'AdvancedMD Link',
      urlPart: 'advancedmd',
      content: <AdvancedMDTabs customerAllData={customerAllData?.customer} />
    });
  }

  const currentIndex = TabContents.findIndex(t => t.urlPart === tab);

  const handleTabChange = (_, index: number) => {
    const newTab = TabContents[index]?.urlPart;
    changeTab(newTab);
  };

  return customerAllData ? (
    <Card>
      <AppBar position="static" color="default">
        <Box display="flex" justifyContent="center">
          <Tabs
            value={currentIndex}
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            onChange={handleTabChange}
            indicatorColor="primary"
          >
            {TabContents.map(
              (t: TabInfo, i: number) =>
                t.access && (
                  <Tab
                    className={classes.tab}
                    label={t.displayName}
                    key={i}
                    {...a11yProps(i)}
                  />
                )
            )}
          </Tabs>
        </Box>
      </AppBar>
      <Grid item className={classes.root}>
        {TabContents.map(
          (t: TabInfo, i: number) =>
            t.access && (
              <TabPanel value={currentIndex} index={i} key={t.urlPart}>
                {t.content}
              </TabPanel>
            )
        )}
      </Grid>
    </Card>
  ) : (
    <CircularProgress />
  );
};
export default React.memo(CustomerProfile);
